import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Play, X } from "lucide-react";
import { PageHead, Reveal } from "@/components/Motion";
import { Kept, Loading, useApi } from "@/components/Common";
import { media } from "@/lib/api";
import { useSite } from "@/context/SiteContext";

const embed = (link) => {
  if (!link) return null;
  const yt = link.match(/(?:youtu\.be\/|v=|embed\/)([\w-]{11})/);
  if (yt) return `https://www.youtube-nocookie.com/embed/${yt[1]}`;
  const vm = link.match(/vimeo\.com\/(\d+)/);
  if (vm) return `https://player.vimeo.com/video/${vm[1]}`;
  return null;
};

export const Player = ({ v, onClose }) => {
  const src = embed(v.link);
  const isVideo = (v.media_kind || v.kind) === "video";
  return (
    <motion.div className="fixed inset-0 z-[85] bg-[#070F0B]/95 backdrop-blur flex items-center justify-center p-4" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} data-lenis-prevent data-testid="voice-player">
      <div className="relative w-full max-w-3xl glass rounded-2xl p-6 md:p-8 max-h-[90vh] overflow-y-auto">
        <button className="absolute top-4 right-4 lb-btn !w-9 !h-9" onClick={onClose} data-testid="voice-player-close"><X size={16} /></button>
        <p className="font-mono-x text-xs tracking-[0.25em] text-[#C9A24B]">{v.person} · {v.year}</p>
        <h3 className="mt-2 font-display text-3xl">{v.title}</h3>
        <div className="mt-6">
          {src ? <iframe title={v.title} src={src} className="w-full aspect-video rounded-lg" allow="autoplay; fullscreen" />
            : v.media_url ? (isVideo ? <video src={media(v.media_url)} controls className="w-full rounded-lg" /> : <audio src={media(v.media_url)} controls className="w-full" data-testid="voice-audio" />)
              : <div className="relative rounded-lg overflow-hidden"><img src={media(v.thumb_url)} alt="Family archive photograph" className="w-full aspect-video object-cover sepia" /><p className="absolute inset-0 grid place-items-center bg-black/55 font-accent italic text-xl">Recording coming soon</p></div>}
        </div>
        {v.transcript && <div className="mt-6"><p className="label">Transcript</p><p className="text-sm leading-relaxed text-[#F6EFE0]/85 whitespace-pre-line">{v.transcript}</p></div>}
      </div>
    </motion.div>
  );
};

export default function Voices() {
  const { session } = useSite() || {};
  const { data, loading } = useApi("/public/list/voices", [session?.active]);
  const [open, setOpen] = useState(null);
  const items = Array.isArray(data?.items) ? data.items : [];
  return (
    <div data-testid="voices-page">
      <PageHead eyebrow="Remember · Family Voices & Videos" title="Family Voices" line="Hear their stories, in their own words." />
      <section className="max-w-7xl mx-auto px-6 md:px-10 pb-28">
        {loading ? <Loading /> : items.length ? (
          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
            {items.map((v, k) => (
              <Reveal key={v.id} delay={k * 0.08}>
                <button className="group w-full text-left card-gold overflow-hidden" onClick={() => setOpen(v)} data-testid={`voice-card-${k}`}>
                  <div className="relative aspect-[16/10] overflow-hidden">
                    <img src={media(v.thumb_url)} alt="Family archive photograph" className="w-full h-full object-cover sepia-[.6] group-hover:sepia-0 group-hover:scale-105 transition-[filter,transform] duration-1000" />
                    <span className="absolute inset-0 grid place-items-center"><span className="grid place-items-center w-16 h-16 rounded-full bg-[#C9A24B] text-[#0B1D14] shadow-[0_0_40px_rgba(201,162,75,.6)] group-hover:scale-110 transition-transform"><Play size={22} /></span></span>
                  </div>
                  <div className="p-6">
                    <p className="font-display text-2xl">{v.title}</p>
                    <p className="mt-1 text-sm text-[#E4C87F]">{v.person} · {v.year}</p>
                  </div>
                </button>
              </Reveal>
            ))}
          </div>
        ) : <Kept title="Their voices are kept safe" text="Recordings are private by default and shared with family only." era="faded" />}
      </section>
      <AnimatePresence>{open && <Player v={open} onClose={() => setOpen(null)} />}</AnimatePresence>
    </div>
  );
}
